import { useQuery } from '@tanstack/react-query'
import { analyticsFetch, getCallerAgentId, hasLiveAnalytics } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  emptyTurnoverReport,
  fromOverview,
  previousRange,
  toTurnoverReport,
  type AnalyticsDailyResponse,
  type AnalyticsTurnoverResponse,
  type TurnoverReport,
} from '@/api/map/turnover'
import type { AnalyticsMemberPnlPage } from '@/api/map/pnl'
import * as api from '@/data/api'
import type { DateRange } from '@/data/types'
import { keys } from '@/api/hooks/shared/keys'

function rangeQuery(range: { from: string; to: string }): string {
  return new URLSearchParams({
    from: range.from.slice(0, 10),
    to: range.to.slice(0, 10),
  }).toString()
}

async function loadTurnoverLive(id: string, range: DateRange): Promise<TurnoverReport> {
  const qs = rangeQuery(range)
  const prior = previousRange(range.from, range.to)
  const priorQs = rangeQuery(prior)

  const [daily, previous, agents, markets, members] = await Promise.all([
    analyticsFetch<AnalyticsDailyResponse>(`/v1/agents/${id}/daily?${qs}`),
    analyticsFetch<AnalyticsDailyResponse>(`/v1/agents/${id}/daily?${priorQs}`).catch(
      () => ({ rows: [] as AnalyticsDailyResponse['rows'] }),
    ),
    analyticsFetch<AnalyticsTurnoverResponse>(
      `/v1/agents/${id}/turnover?${qs}&groupBy=agent`,
    ).catch(() => ({ groupBy: 'agent' as const, rows: [] })),
    analyticsFetch<AnalyticsTurnoverResponse>(
      `/v1/agents/${id}/turnover?${qs}&groupBy=market`,
    ).catch(() => ({ groupBy: 'market' as const, rows: [] })),
    analyticsFetch<AnalyticsMemberPnlPage>(
      `/v1/agents/${id}/member-pnl?${qs}&page=0&size=10&sort=turnover`,
    ).catch(() => ({ rows: [] as AnalyticsMemberPnlPage['rows'] })),
  ])

  return toTurnoverReport({
    daily,
    previous,
    agents,
    markets,
    members: members.rows ?? [],
    rootId: id,
  })
}

async function loadTurnover(query: api.OverviewQuery): Promise<TurnoverReport | null> {
  const { range } = query
  if (hasLiveAnalytics()) {
    // Scope is honoured live because the analytics routes are per-agent; sport
    // and game are not in the contract, so the filter bar narrows them
    // client-side rather than the page pretending the server did it.
    const id = query.rootId ?? getCallerAgentId()
    if (!id) return emptyTurnoverReport()
    try {
      return await loadTurnoverLive(id, range)
    } catch (err) {
      if (isUnreachableApiError(err)) return emptyTurnoverReport()
      throw err
    }
  }

  const snapshot = await api.getOverview(query)
  // `null` is "that scope is not beneath you", and it has to survive to the
  // screen as null — fabricating an empty report would draw a zeroed book under
  // someone else's name.
  return snapshot ? fromOverview(snapshot) : null
}

export function useTurnover(query: api.OverviewQuery) {
  return useQuery({
    queryKey: keys.turnover(query),
    queryFn: () => loadTurnover(query),
    placeholderData: (previous) => previous,
  })
}
